import {FC, useEffect, useMemo, useState} from "react";
import {Typography} from "antd";
import {CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined, FieldTimeOutlined} from "@ant-design/icons";
import {HasMatchStarted, IMatch} from "../Interfaces/IMatch";
import {IUserBets} from "../Interfaces/IUserBets";
import {matchAsUtcDate} from "../Services/datetime";

const {Text} = Typography;

type Props = {
    matches: IMatch[];
    userBets: IUserBets[];
};

const DAY_MS = 24 * 60 * 60 * 1000;

const formatCountdown = (ms: number): string => {
    if (ms <= 0) return 'en cours';
    const total = Math.floor(ms / 1000);
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (d > 0) return `${d}j ${h}h ${m}m`;
    if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
    return `${m}m ${String(s).padStart(2, '0')}s`;
};

const labelStyle: React.CSSProperties = {
    fontSize: 10,
    letterSpacing: 0.5,
    color: 'rgba(255,255,255,0.55)',
    textTransform: 'uppercase',
};
const valueStyle: React.CSSProperties = {fontSize: 12, color: 'rgba(255,255,255,0.9)'};
const rowStyle: React.CSSProperties = {display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap'};
const rowsWrap: React.CSSProperties = {display: 'flex', flexWrap: 'wrap', justifyContent: 'center', columnGap: 16, rowGap: 2, marginTop: 2};
const sectionStyle: React.CSSProperties = {minWidth: 0, textAlign: 'center'};

const BetsSidebarStats: FC<Props> = ({matches, userBets}) => {
    const [now, setNow] = useState<number>(() => Date.now());

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const {fullyBet, remaining} = useMemo(() => {
        // "Paris saisis" includes EVERY match where the user posted both
        // scores, regardless of whether the kickoff is past — once a bet is
        // saved it stays on the user's tally.
        // "À parier" only counts matches still open for input (not started)
        // where at least one of the two scores is missing.
        const playable = matches.filter(m =>
            m.team_idt_Dom && m.team_idt_Ext &&
            m.MatchTeam_Idt_Dom && m.MatchTeam_Idt_Ext,
        );
        let full = 0;
        let pending = 0;
        for (const m of playable) {
            const hasDom = userBets.some(b => b.matchteam_idt === m.MatchTeam_Idt_Dom);
            const hasExt = userBets.some(b => b.matchteam_idt === m.MatchTeam_Idt_Ext);
            if (hasDom && hasExt) full++;
            else if (!HasMatchStarted(m)) pending++;
        }
        return {fullyBet: full, remaining: pending};
    }, [matches, userBets]);

    const upcoming = useMemo(() => {
        return matches
            .map(m => ({m, t: matchAsUtcDate(m.Dte, m.Hre)?.getTime() ?? NaN}))
            .filter(x => Number.isFinite(x.t) && x.t > now)
            .sort((a, b) => a.t - b.t);
    }, [matches, now]);

    const next24hCount = upcoming.filter(x => x.t - now <= DAY_MS).length;
    const nextMatch = upcoming[0];

    return (
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, padding: '8px 12px'}}>
            <div style={sectionStyle}>
                <Text style={labelStyle}>Mes paris — compétition</Text>
                <div style={rowsWrap}>
                    <div style={rowStyle}>
                        <CheckCircleOutlined style={{color: '#66bb6a'}}/>
                        <Text style={valueStyle}>
                            {fullyBet} pari{fullyBet > 1 ? 's' : ''} saisi{fullyBet > 1 ? 's' : ''}
                        </Text>
                    </div>
                    <div style={rowStyle}>
                        <ExclamationCircleOutlined style={{color: '#ef5350'}}/>
                        <Text style={valueStyle}>
                            {remaining} à parier
                        </Text>
                    </div>
                </div>
            </div>

            <div style={sectionStyle}>
                <Text style={labelStyle}>Prochaines 24 h</Text>
                <div style={rowsWrap}>
                    <div style={rowStyle}>
                        <FieldTimeOutlined style={{color: '#42a5f5'}}/>
                        <Text style={valueStyle}>
                            {next24hCount} match{next24hCount > 1 ? 's' : ''} à venir
                        </Text>
                    </div>
                    <div style={rowStyle}>
                        <ClockCircleOutlined style={{color: 'rgba(255,255,255,0.7)'}}/>
                        <Text style={valueStyle}>
                            {nextMatch ? `prochain dans ${formatCountdown(nextMatch.t - now)}` : 'aucun à venir'}
                        </Text>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BetsSidebarStats;
